/**
 * Interactive forecast chart with three series plotted on two y axes:
 *   0 - Implied volatility (left axis, 0-100)
 *   1 - Price             (right axis)
 *   2 - Difference        (right axis, derived from 0 and 1; read-only)
 *
 * Series 0 and 1 are drag-editable via chartjs-plugin-dragdata; series 2 is
 * recomputed from them and is deliberately locked against dragging.
 *
 * Note: this is a scaffolding/demo component -- expiries and the volatility
 * series are simulated rather than fetched from the options API.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart, registerables, Tooltip } from 'chart.js';
import 'chartjs-plugin-dragdata';

// Pull in every built-in Chart.js element/scale/controller. Convenient here;
// the heavier screens register only what they use.
Chart.register(...registerables);

const OptionsForecastChart = () => {
  const [expiries, setExpiries] = useState([]);

  // Fixed price band; its midpoint seeds the flat starting Price series so the
  // user has a sensible line to drag away from.
  const range = [148, 262.5];
  const averagePrice = (range[0] + range[1]) / 2;

  // Stand-in for the real expiries endpoint. Runs once on mount and drives the
  // label-building effect below.
  useEffect(() => {
    // Simulate fetching expiries from an API or other source
    const fetchExpiries = async () => {
      // Simulate a delay
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setExpiries(["2026-01-16", "2027-01-15"]);
    };

    fetchExpiries();
  }, []);

  // Furthest expiry in the list -- the chart's right-hand time boundary.
  // Dates are compared as epoch ms so string ordering never comes into play.
  const getMaxExpiryDate = (expiries) => {
    const maxExpiryDate = new Date(Math.max(...expiries.map(date => new Date(date).getTime())));
    return maxExpiryDate;
  };

  // Weekly YYYY-MM-DD labels from today through maxDate inclusive.
  // NOTE: currentDate is mutated in place by setDate, which is what advances
  // the loop -- do not hoist it out or the loop will not terminate.
  const generateWeeklyDates = (maxDate) => {
    const dates = [];
    const currentDate = new Date();
    while (currentDate <= maxDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 7);
    }
    return dates;
  };

  // Placeholder implied-volatility series: `length` integers in [min, max].
  const generateRandomDataPoints = (length, min, max) => {
    return Array.from({ length }, () => Math.floor(Math.random() * (max - min + 1)) + min);
  };

  // Chart.js data object held in state so drag edits and the derived
  // difference series can be written back into it. Dataset order is load
  // bearing -- the effects below address them by index (0/1/2).
  const [data, setData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Implied volatility',
        backgroundColor: 'rgba(75,192,192,0.4)',
        borderColor: 'rgba(75,192,192,1)',
        pointBackgroundColor: 'rgba(75,192,192,1)',
        pointBorderColor: '#fff',
        data: [],
        pointHitRadius: 45, // Increase hit radius for easier selection
        yAxisID: 'y-axis-1',
      },
      {
        label: 'Price',
        backgroundColor: 'rgba(192,75,75,0.4)',
        borderColor: 'rgba(192,75,75,1)',
        pointBackgroundColor: 'rgba(192,75,75,1)',
        pointBorderColor: '#fff',
        data: [],
        pointHitRadius: 45, // Increase hit radius for easier selection
        yAxisID: 'y-axis-2',
      },
      {
        label: 'Difference dataset',
        backgroundColor: 'rgba(75,75,192,0.4)',
        borderColor: 'rgba(75,75,192,1)',
        pointBackgroundColor: 'rgba(75,75,192,1)',
        pointBorderColor: '#fff',
        data: [],
        pointHitRadius: 25, // Increase hit radius for easier selection
        yAxisID: 'y-axis-2',
        dragData: false, // Make the difference dataset non-draggable
      },
    ],
  });

  // Once expiries arrive, lay out the weekly x axis and seed series 0 and 1.
  // Series 2 is left alone here; the effect below fills it in.
  useEffect(() => {
    if (expiries.length > 0) {
      const maxExpiryDate = getMaxExpiryDate(expiries);
      const labels = generateWeeklyDates(maxExpiryDate);
      const impliedVolatilityData = generateRandomDataPoints(labels.length, 40, 100);
      const priceData = Array(labels.length).fill(averagePrice);

      setData((prevData) => ({
        labels: labels,
        datasets: prevData.datasets.map((dataset, index) => {
          if (index === 0) {
            return { ...dataset, data: impliedVolatilityData };
          } else if (index === 1) {
            return { ...dataset, data: priceData };
          } else {
            return dataset;
          }
        }),
      }));
    }
  }, [expiries]);

  // Recompute the derived "Difference" series whenever the labels or either
  // editable series change -- including after a drag.
  //
  // Guarded by the index === 2 check when writing back: only series 2 is
  // replaced, so this cannot feed back into its own dependencies and loop.
  useEffect(() => {
    if (data.labels.length > 0) {
      const { datasets, labels } = data;
      const impliedVolatilityData = datasets[0].data;
      const priceData = datasets[1].data;
      const differenceDataset = [];

      // difference = price - impliedVolatility + days elapsed since the first
      // label, so the series drifts upward with time to expiry.
      for (let i = 0; i < labels.length; i++) {
        const dateDifference = new Date(labels[i]).getTime() - new Date(labels[0]).getTime();
        const dateDifferenceInDays = dateDifference / (1000 * 3600 * 24);
        differenceDataset.push(priceData[i] - impliedVolatilityData[i] + dateDifferenceInDays);
      }

      setData((prevData) => ({
        ...prevData,
        datasets: prevData.datasets.map((dataset, index) => {
          if (index === 2) {
            return { ...dataset, data: differenceDataset };
          }
          return dataset;
        }),
      }));
    }
  }, [data.labels, data.datasets[0].data, data.datasets[1].data]);

  // The right-hand axis is scaled to the derived series, padded by 50 either
  // side so the line never sits flush against the plot edge.
  const differenceValues = data.datasets[2].data.filter(value => value !== null);
  const minDifference = Math.min(...differenceValues);
  const maxDifference = Math.max(...differenceValues);

  const options = {
    responsive: true,
    scales: {
      x: {
        type: 'category',
        grid: {
          color: '#363636',
        },
      },
      'y-axis-1': {
        beginAtZero: true,
        min: 0,
        max: 100,
        position: 'left',
        grid: {
          color: '#363636',
        },
      },
      'y-axis-2': {
        beginAtZero: true,
        min: minDifference - 50,
        max: maxDifference + 50,
        position: 'right',
        grid: {
          drawOnChartArea: false, // only want the grid lines for one axis to show up
          color: '#363636',
        },
      },
    },
    plugins: {
      legend: {
        display: true,
      },
      // Tooltip shows the hovered series first, then every other series at the
      // same x index, so all three values can be compared at a glance.
      tooltip: {
        callbacks: {
          label: function (context) {
            const dataIndex = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            const dataset = context.chart.data.datasets[datasetIndex];
            return `${dataset.label}: ${dataset.data[dataIndex]}`;
          },
          afterLabel: function (context) {
            const dataIndex = context.dataIndex;
            const datasets = context.chart.data.datasets;
            const result = [];
            datasets.forEach((dataset, index) => {
              if (index !== context.datasetIndex) {
                result.push(`${dataset.label}: ${dataset.data[dataIndex]}`);
              }
            });
            return result;
          },
        },
      },
      // Point dragging. Series 2 is derived, so both handlers bail out early
      // for datasetIndex 2 rather than relying on its dragData: false flag.
      dragData: {
        round: 1,
        showTooltip: true,
        onDragStart: (e, datasetIndex, index, value) => {
          if (datasetIndex === 2) return; // Prevent dragging for the difference dataset
          e.target.style.cursor = 'grabbing';
        },
        onDragEnd: (e, datasetIndex, index, value) => {
          if (datasetIndex === 2) return; // Prevent dragging for the difference dataset
          e.target.style.cursor = 'default';
          // Replace just the dragged point, then merge that array back into
          // the dataset immutably so React sees a new object and re-renders.
          const newData = data.datasets[datasetIndex].data.map((d, i) => (i === index ? value : d));
          setData((prevData) => ({
            ...prevData,
            datasets: prevData.datasets.map((dataset, i) => {
              if (i === datasetIndex) {
                return {
                  ...dataset,
                  data: newData,
                };
              }
              return dataset;
            }),
          }));
        },
      },
    },
  };

  // Held for imperative Chart.js access; not currently read.
  const chartRef = useRef(null);

  return (
    <div>
      <h2>Options Forecast Chart</h2>
      <Line
        ref={chartRef}
        data={data}
        options={options}
      />
    </div>
  );
};

export default OptionsForecastChart;